import React, { useEffect, useState, useCallback } from "react";
import { Users, Mail, Send, Search, X, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { VipBadge } from "./VipTierCard";
import { ModalPortal } from "./ModalPortal";

const SEGMENTS = [
    { slug: "all", label: "All customers", desc: "Every registered customer" },
    { slug: "first_time", label: "First-time buyers", desc: "Exactly 1 paid order" },
    { slug: "repeat", label: "Repeat customers", desc: "2 or more paid orders" },
    { slug: "vip", label: "VIP members", desc: "Bronze and above" },
    { slug: "abandoned_cart", label: "Registered, never bought", desc: "Created account but no paid order yet" },
    { slug: "unused_credit", label: "Has unused store credit", desc: "Balance > A$0" },
    { slug: "inactive_30", label: "Inactive 30+ days", desc: "Bought once, then quiet for 30+ days" },
    { slug: "inactive_60", label: "Inactive 60+ days", desc: "" },
    { slug: "inactive_90", label: "Inactive 90+ days", desc: "" },
    { slug: "inactive_180", label: "Inactive 180+ days", desc: "" },
];

const TIERS = [
    { slug: "all", label: "All tiers" },
    { slug: "none", label: "Researcher" },
    { slug: "bronze", label: "Bronze" },
    { slug: "silver", label: "Silver" },
    { slug: "gold", label: "Gold" },
    { slug: "platinum", label: "Platinum" },
    { slug: "diamond", label: "Diamond" },
];

export const AdminSegmentsPanel = ({ token }) => {
    const headers = { "X-Admin-Token": token };
    const [segment, setSegment] = useState("all");
    const [tier, setTier] = useState("all");
    const [minSpend, setMinSpend] = useState("");
    const [productSlug, setProductSlug] = useState("");
    const [products, setProducts] = useState([]);
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showBlast, setShowBlast] = useState(false);
    const [showCampaigns, setShowCampaigns] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/products");
                setProducts(data);
            } catch { /* non-fatal */ }
        })();
    }, []);

    const preview = useCallback(async () => {
        setLoading(true);
        try {
            const params = { segment, limit: 500 };
            if (tier && tier !== "all") params.tier = tier;
            if (minSpend) params.min_spend = parseFloat(minSpend);
            if (productSlug) params.product_slug = productSlug;
            const { data } = await api.get("/admin/segments/preview", { params, headers });
            setResults(data);
        } catch {
            toast.error("Could not load segment");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [segment, tier, minSpend, productSlug, token]);

    useEffect(() => { preview(); }, [preview]);

    const currentSeg = SEGMENTS.find((s) => s.slug === segment);

    return (
        <div className="space-y-5" data-testid="admin-segments-panel">
            {/* Segment picker */}
            <div className="glass-elevated rounded-2xl p-5">
                <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-3">Pick a segment</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                    {SEGMENTS.map((s) => (
                        <button
                            key={s.slug}
                            onClick={() => setSegment(s.slug)}
                            className={`text-left rounded-xl p-3 border transition-colors ${segment === s.slug ? "border-[var(--gold)]/50 bg-[rgba(233,188,11,0.06)]" : "border-white/10 hover:border-white/30 bg-black/30"}`}
                            data-testid={`segment-${s.slug}`}
                        >
                            <p className={`text-sm font-bold ${segment === s.slug ? "text-[var(--gold)]" : "text-white"}`}>{s.label}</p>
                            {s.desc && <p className="text-[10px] text-[var(--text-muted)] mt-1">{s.desc}</p>}
                        </button>
                    ))}
                </div>
            </div>

            {/* Additional filters */}
            <div className="glass rounded-2xl p-4 flex flex-wrap items-center gap-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Refine:</div>
                <select value={tier} onChange={(e) => setTier(e.target.value)} className="px-3 py-2 bg-black/40 border border-white/10 rounded-full text-xs font-mono uppercase tracking-widest text-white focus:outline-none focus:border-[var(--gold)]" data-testid="segment-tier">
                    {TIERS.map((t) => <option key={t.slug} value={t.slug}>{t.label}</option>)}
                </select>
                <input
                    value={minSpend}
                    onChange={(e) => setMinSpend(e.target.value)}
                    placeholder="Min lifetime A$"
                    type="number"
                    className="w-36 px-3 py-2 bg-black/40 border border-white/10 rounded-full text-xs text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                    data-testid="segment-min-spend"
                />
                <select value={productSlug} onChange={(e) => setProductSlug(e.target.value)} className="px-3 py-2 bg-black/40 border border-white/10 rounded-full text-xs text-white max-w-[200px] focus:outline-none focus:border-[var(--gold)]" data-testid="segment-product">
                    <option value="">Any product</option>
                    {products.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                </select>
                <button onClick={preview} className="px-3 py-2 rounded-full text-xs font-mono uppercase tracking-widest border border-white/10 text-[var(--text-secondary)] hover:text-white hover:border-white/30 inline-flex items-center gap-1" data-testid="segment-refresh">
                    <RefreshCw size={12} /> Refresh
                </button>
                <div className="ml-auto flex gap-2">
                    <button onClick={() => setShowCampaigns(true)} className="px-3 py-2 rounded-full text-xs font-mono uppercase tracking-widest border border-white/10 text-[var(--text-secondary)] hover:text-white hover:border-white/30" data-testid="view-campaigns">
                        Past campaigns
                    </button>
                    <button
                        onClick={() => setShowBlast(true)}
                        disabled={!results || results.total === 0}
                        className="btn-solid disabled:opacity-40"
                        data-testid="segment-email-blast"
                    >
                        <Mail size={12} /> Email this segment
                    </button>
                </div>
            </div>

            {/* Results */}
            <div className="glass-elevated rounded-2xl overflow-hidden" data-testid="segment-results">
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
                    <div>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{currentSeg?.label}</p>
                        <p className="font-display text-2xl font-extrabold text-white italic tracking-tighter">
                            {loading ? "Loading…" : `${results?.total || 0} customer${results?.total === 1 ? "" : "s"}`}
                        </p>
                    </div>
                    <Users size={28} className="text-[var(--gold)]" />
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                    {(results?.customers || []).map((c) => (
                        <div key={c.user_id} className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-3 px-5 py-3 border-b border-white/5" data-testid={`segment-customer-${c.user_id}`}>
                            <div className="min-w-0">
                                <p className="text-sm text-white truncate">{c.name || c.email.split("@")[0]}</p>
                                <p className="text-[10px] text-[var(--text-muted)] font-mono truncate">{c.email}</p>
                            </div>
                            <div className="flex items-center"><VipBadge tier={c.vip_tier} size="sm" /></div>
                            <p className="text-sm font-mono text-[var(--gold)]">A${c.lifetime_spend.toFixed(0)}</p>
                            <p className="text-sm font-mono text-emerald-300">A${c.store_credit_balance.toFixed(0)}</p>
                            <p className="text-[10px] text-[var(--text-muted)] font-mono">
                                {c.paid_orders} orders {c.days_since_purchase !== null ? `· ${c.days_since_purchase}d ago` : ""}
                            </p>
                        </div>
                    ))}
                    {results?.customers?.length === 0 && (
                        <p className="text-center text-[var(--text-secondary)] py-12 text-sm">No customers match this segment.</p>
                    )}
                </div>
            </div>

            {showBlast && (
                <EmailBlastModal
                    token={token}
                    segment={segment}
                    tier={tier}
                    minSpend={minSpend}
                    productSlug={productSlug}
                    targetCount={results?.total || 0}
                    onClose={() => setShowBlast(false)}
                />
            )}
            {showCampaigns && <CampaignsModal token={token} onClose={() => setShowCampaigns(false)} />}
        </div>
    );
};

const EmailBlastModal = ({ token, segment, tier, minSpend, productSlug, targetCount, onClose }) => {
    const headers = { "X-Admin-Token": token };
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("<p>Hi {{first_name}},</p>\n<p>We've got something new for you …</p>");
    const [testEmail, setTestEmail] = useState("");
    const [busy, setBusy] = useState(false);

    const send = async (testMode) => {
        if (!subject.trim() || !body.trim()) return toast.error("Subject and body are required");
        if (!testMode && !window.confirm(`Send to ${targetCount} customers? This cannot be undone.`)) return;
        setBusy(true);
        try {
            const payload = {
                segment, subject, body_html: body,
                tier: tier !== "all" ? tier : undefined,
                min_spend: minSpend ? parseFloat(minSpend) : undefined,
                product_slug: productSlug || undefined,
                test_mode: testMode,
                test_email: testMode ? (testEmail || undefined) : undefined,
            };
            const { data } = await api.post("/admin/segments/email-blast", payload, { headers });
            toast.success(`Sent ${data.sent}/${data.recipients}${data.failed ? ` · ${data.failed} failed` : ""}`);
            if (!testMode) onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Blast failed");
        }
        setBusy(false);
    };

    return (
        <ModalPortal>
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="glass-elevated max-w-2xl w-full rounded-2xl p-6 max-h-[90vh] overflow-y-auto" data-testid="email-blast-modal">
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Targeted campaign</p>
                        <h2 className="font-display text-2xl font-extrabold text-white italic tracking-tighter">Email {targetCount} customer{targetCount === 1 ? "" : "s"}</h2>
                    </div>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-white"><X size={18} /></button>
                </div>

                <div className="space-y-4">
                    <div>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Subject *</p>
                        <input
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="A limited drop, just for our regulars"
                            className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                            data-testid="blast-subject"
                        />
                    </div>
                    <div>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Body HTML *</p>
                        <textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            rows={10}
                            className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white font-mono focus:outline-none focus:border-[var(--gold)]"
                            data-testid="blast-body"
                        />
                        <p className="text-[10px] text-[var(--text-muted)] mt-1">
                            HTML allowed. The Carbon Labs header/footer is added automatically.
                        </p>
                    </div>
                    <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-3 flex items-start gap-2">
                        <AlertTriangle size={14} className="text-amber-300 mt-0.5 shrink-0" />
                        <div>
                            <p className="text-xs text-white">Test first.</p>
                            <p className="text-[10px] text-[var(--text-muted)]">Send to yourself before blasting {targetCount} recipients.</p>
                            <div className="flex gap-2 mt-2">
                                <input
                                    value={testEmail}
                                    onChange={(e) => setTestEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="flex-1 px-3 py-1.5 bg-black/40 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-[var(--gold)]"
                                    data-testid="blast-test-email"
                                />
                                <button onClick={() => send(true)} disabled={busy} className="px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest border border-amber-500/40 text-amber-300 disabled:opacity-40" data-testid="blast-send-test">
                                    Send test
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} className="px-4 py-2 rounded-full border border-white/10 text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)]">Cancel</button>
                    <button onClick={() => send(false)} disabled={busy || targetCount === 0} className="btn-solid disabled:opacity-50" data-testid="blast-send-all">
                        <Send size={12} /> {busy ? "Sending…" : `Send to ${targetCount}`}
                    </button>
                </div>
            </div>
        </div>
        </ModalPortal>
    );
};

const CampaignsModal = ({ token, onClose }) => {
    const headers = { "X-Admin-Token": token };
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/email-campaigns", { headers });
                setCampaigns(data.campaigns);
            } catch {
                toast.error("Could not load campaigns");
            }
            setLoading(false);
        })();
        // eslint-disable-next-line
    }, [token]);

    return (
        <ModalPortal>
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="glass-elevated max-w-2xl w-full rounded-2xl p-6 max-h-[80vh] overflow-y-auto" data-testid="campaigns-modal">
                <div className="flex items-center justify-between mb-5">
                    <h2 className="font-display text-2xl font-extrabold text-white italic tracking-tighter">Past campaigns</h2>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-white"><X size={18} /></button>
                </div>
                {loading && <p className="text-sm text-[var(--text-secondary)] text-center py-6">Loading…</p>}
                {!loading && campaigns.length === 0 && <p className="text-sm text-[var(--text-secondary)] text-center py-6">No campaigns sent yet.</p>}
                <div className="space-y-2">
                    {campaigns.map((c) => (
                        <div key={c.id} className="p-3 rounded-xl bg-black/40 border border-white/5">
                            <div className="flex items-center justify-between mb-1">
                                <p className="text-sm text-white font-bold truncate flex-1">{c.subject}</p>
                                {c.test_mode && <span className="text-[9px] font-mono uppercase tracking-widest text-amber-300 ml-2">TEST</span>}
                            </div>
                            <p className="text-[10px] text-[var(--text-muted)] font-mono">
                                {c.segment} · {c.sent_count}/{c.recipients_count} sent
                                {c.failed_count > 0 ? ` · ${c.failed_count} failed` : ""}
                                · {new Date(c.sent_at).toLocaleString("en-AU")}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
        </ModalPortal>
    );
};
